import { useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import {
  useDataProvider,
  useLocalization,
  useSnackbar,
  useUserAccess,
} from '@ui-framework/index';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowTab,
  ArrowTabGroup,
  CustomTabPanel,
  IconLibrary,
  PageSection,
  AvailabilitySlotTypeProvider,
} from '@product-framework/index';
import { FormattedMessage } from 'react-intl';
import { AVAILABILITY_FORM_VIEW } from '../../constants';
import SPAvailabilityStepperConfig from './SPAvailabilityStepperConfig';
import FooterNavigation from '../common/FooterNavigation';

function Availability({
  serviceProviderId,
  subSectionType = 'DEFAULT',
  entityId = null,
  currentIndex = null,
}) {
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { put, get, getApiUrl, post } = useDataProvider(provider);
  const snackbar = useSnackbar();
  const { formatMessage } = useLocalization();
  const userAccess = useUserAccess();
  const navigate = useNavigate();
  const { id: pageId, tabNumber, ...others } = useParams();

  const subTabNumber =
    subSectionType === 'LOCATION'
      ? others?.subTabSubSectionNumber
      : others.action;

  const locationId = entityId;

  const getValidTabNumber = tNum => {
    if (tNum <= 1) {
      return 1;
    }
    const validTab = [
      AVAILABILITY_FORM_VIEW.LIVE_CALENDAR,
      AVAILABILITY_FORM_VIEW.DEFAULT_SCHEDULES,
      AVAILABILITY_FORM_VIEW.SPECIAL_SCHEDULES,
    ].includes(tNum);
    return validTab ? tNum : 1;
  };

  const parsedSubTabNumber = subTabNumber ? parseInt(subTabNumber, 10) : 1;
  const validTabNumber = getValidTabNumber(parsedSubTabNumber);
  const currentTabValue = validTabNumber - 1;

  const commonParams = {
    put,
    get,
    getApiUrl,
    post,
    snackbar,
    pageId: serviceProviderId,
    formatMessage,
  };

  const stepperTabs = useMemo(
    () => SPAvailabilityStepperConfig(IconLibrary, userAccess),
    [userAccess],
  );

  const tabs = useMemo(
    () =>
      stepperTabs.map(stepper => (
        <ArrowTab
          key={stepper.label}
          maxTabs={stepperTabs.length}
          label={stepper.label}
          config={stepper}
          arrowHeadArea={stepper?.arrowHeadArea}
          arrowBackArea={stepper?.arrowBackArea}
        />
      )),
    [stepperTabs],
  );

  const onMainTabChange = useCallback(
    newValue => {
      if (subSectionType === 'LOCATION') {
        navigate(
          `/serviceproviders/edit/${pageId}/${tabNumber}/edit/${locationId}/${currentIndex + 1}/${newValue + 1}`,
        );
      } else {
        navigate(
          `/serviceproviders/edit/${pageId}/${tabNumber}/${newValue + 1}`,
        );
      }
    },
    [navigate, pageId],
  );

  const ActiveStepper = stepperTabs[currentTabValue];

  return (
    <PageSection
      label={
        subSectionType !== 'LOCATION' ? (
          <FormattedMessage
            id="SERVICE_PROVIDER_AVAILABILITY"
            defaultMessage="Service Provider Availability"
          />
        ) : (
          <FormattedMessage
            id="LOCATION_AVAILABILITY"
            defaultMessage="Location Availability"
          />
        )
      }>
      <Box>
        <ArrowTabGroup
          inner={subSectionType !== 'LOCATION'}
          value={currentTabValue}
          onChange={(event, newValue) => {
            onMainTabChange(Number(newValue));
          }}>
          {tabs}
        </ArrowTabGroup>
        <Box>
          <AvailabilitySlotTypeProvider>
            <CustomTabPanel value={currentTabValue} index={currentTabValue}>
              <ActiveStepper.Component
                setTabValue={tabNo => onMainTabChange(tabNo)}
                serviceProviderId={pageId}
                currentIndex={currentTabValue}
                stepperTabs={stepperTabs}
                action={null}
                entityId={locationId}
                commonParams={commonParams}
                locationId={locationId}
              />
              <FooterNavigation
                inner={subSectionType !== 'LOCATION'}
                onChange={newValue => {
                  onMainTabChange(newValue);
                }}
                currentIndex={currentTabValue}
                stepperTabs={stepperTabs}
              />
            </CustomTabPanel>
          </AvailabilitySlotTypeProvider>
        </Box>
      </Box>
    </PageSection>
  );
}
export default Availability;
